// Bold Pop form controls (manual §14, B3): the dropdown, the calendar and the time picker drawn in
// the brand instead of the operating system's grey menus. Each one is a drop-in for the native
// element it replaces: same id (so <label htmlFor> still names it), same `value`, and `onChange`
// receives `{ target: { value } }`, so `onChange={(e) => set(e.target.value)}` keeps working.
//
// Trigger: the .input field (3px Ink edge, radius 16) with a chevron; open = Cart Blue edge.
// Popover: a raised card, 3px edge, hard 6px Ink lift (never a soft shadow, §19), pops in on the
// kernel spring. Rows are pills: the keyboard/hover row is Pop Yellow with Ink text, the chosen row
// is Ink with Cream text and a check. Keyboard: arrows, Home/End, PageUp/PageDown, Enter/Space,
// Escape, Tab, and type-to-jump. Long lists (more than 10) get a filter box.
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import '../styles/controls.css';

export type ChangeLike = { target: { value: string } };
export type SelectOption = { value: string; label: string; disabled?: boolean };

type FieldProps = {
  id?: string;
  value: string;
  onChange(e: ChangeLike): void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
  'aria-label'?: string;
};

function textOf(n: ReactNode): string {
  return Children.toArray(n)
    .map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x) : isValidElement(x) ? textOf((x as ReactElement<{ children?: ReactNode }>).props.children) : ''))
    .join('');
}

/** Reads `<option>` children (arrays and fragments included) into plain options. */
export function optionsFrom(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  const walk = (n: ReactNode) =>
    Children.forEach(n, (c) => {
      if (!isValidElement(c)) return;
      const el = c as ReactElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>;
      if (el.type === 'option') {
        const label = textOf(el.props.children);
        out.push({ value: el.props.value === undefined ? label : String(el.props.value), label, disabled: !!el.props.disabled });
      } else walk(el.props.children);
    });
  walk(children);
  return out;
}

/** The trigger's classes: always `input pick`, plus the caller's extras (its own `input` dropped). */
const pickClass = (extra?: string) => ['input', 'pick', ...(extra ?? '').split(/\s+/).filter((c) => c && c !== 'input')].join(' ');

// ── Popover plumbing: a fixed-position layer in a portal so cards and dialogs never clip it ──────
type Place = { left: number; top?: number; bottom?: number; width: number; maxHeight: number };

function usePlacement(open: boolean, anchor: React.RefObject<HTMLElement | null>, minWidth = 0, maxH = 9999) {
  const [place, setPlace] = useState<Place | null>(null);
  const measure = useCallback(() => {
    const el = anchor.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, gap = 8;
    const width = Math.max(r.width, minWidth);
    const left = Math.max(8, Math.min(r.left, vw - width - 8));
    const below = vh - r.bottom - gap - 8, above = r.top - gap - 8;
    if (below >= 260 || below >= above) setPlace({ left, top: r.bottom + gap, width, maxHeight: Math.min(maxH, Math.max(160, below)) });
    else setPlace({ left, bottom: vh - r.top + gap, width, maxHeight: Math.min(maxH, Math.max(160, above)) });
  }, [anchor, minWidth, maxH]);
  useLayoutEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);
  return place;
}

function useOutside(open: boolean, refs: React.RefObject<HTMLElement | null>[], close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (refs.some((r) => r.current?.contains(e.target as Node))) return;
      close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, refs, close]);
}

function Layer({ place, children, className, labelledBy, role, id }: { place: Place | null; children: ReactNode; className: string; labelledBy?: string; role?: string; id?: string }) {
  if (typeof document === 'undefined' || !place) return null;
  const style: CSSProperties = { left: place.left, top: place.top, bottom: place.bottom, minWidth: place.width, maxHeight: place.maxHeight };
  return createPortal(
    <div id={id} className={`pop-layer ${className}`} style={style} role={role} aria-labelledby={labelledBy} data-up={place.bottom !== undefined || undefined}>
      {children}
    </div>,
    document.body,
  );
}

// ── Select ──────────────────────────────────────────────────────────────────────────────────────
export function Select(props: FieldProps & { children?: ReactNode; options?: SelectOption[]; searchable?: boolean; render?: (o: SelectOption) => ReactNode }) {
  const { id: idProp, value, onChange, className, disabled, placeholder, children } = props;
  const auto = useId();
  const id = idProp ?? `sel${auto}`;
  const listId = `${id}-list`;
  const options = useMemo(() => props.options ?? optionsFrom(children), [props.options, children]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState('');
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const typed = useRef({ s: '', t: 0 });
  const searchable = props.searchable ?? options.length > 10;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const current = options.find((o) => o.value === value);
  const place = usePlacement(open, trigger, 220, 360);
  const centred = useRef(false);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setQuery('');
    if (refocus) trigger.current?.focus();
  }, []);
  useOutside(open, [trigger, layer], () => close(false));

  const openList = (at?: 'first' | 'last') => {
    if (disabled) return;
    const sel = options.findIndex((o) => o.value === value);
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    setActive(at === 'first' ? enabled[0] ?? -1 : at === 'last' ? enabled[enabled.length - 1] ?? -1 : sel >= 0 ? sel : enabled[0] ?? -1);
    setOpen(true);
  };
  const choose = (o: SelectOption | undefined) => {
    if (!o || o.disabled) return;
    if (o.value !== value) onChange({ target: { value: o.value } });
    close();
  };

  // Focus the filter (or the list) once open; keep the active row in view.
  useEffect(() => {
    // The layer mounts one render after `open` (it waits for its placement), hence `place`.
    if (!open || !place) return;
    (searchable ? search.current : list.current)?.focus({ preventScroll: true });
  }, [open, searchable, place]);
  useEffect(() => {
    if (!open || !place || active < 0) {
      if (!open) centred.current = false;
      return;
    }
    // On open the chosen row sits in the middle; after that, arrows only nudge it into view.
    list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView?.({ block: centred.current ? 'nearest' : 'center' });
    centred.current = true;
  }, [open, active, shown, place]);
  useEffect(() => {
    if (open) setActive((a) => (shown.length && !shown.some((_, i) => i === a) ? 0 : shown.length ? a : -1));
  }, [shown, open]);

  const move = (from: number, step: number) => {
    let i = from;
    for (let n = 0; n < shown.length; n++) {
      i = Math.max(0, Math.min(shown.length - 1, i + step));
      if (!shown[i]?.disabled) return i;
      if (i === 0 || i === shown.length - 1) break;
    }
    return from;
  };
  const jump = (ch: string) => {
    const now = Date.now();
    typed.current = { s: now - typed.current.t < 700 ? typed.current.s + ch : ch, t: now };
    const q = typed.current.s.toLowerCase();
    const start = q.length === 1 ? active + 1 : active;
    for (let n = 0; n < shown.length; n++) {
      const i = (Math.max(0, start) + n) % shown.length;
      const o = shown[i];
      if (o && !o.disabled && o.label.toLowerCase().startsWith(q)) return i;
    }
    return -1;
  };

  const onListKey = (e: KeyboardEvent<HTMLElement>) => {
    const k = e.key;
    if (k === 'ArrowDown') setActive((a) => move(a, 1));
    else if (k === 'ArrowUp') setActive((a) => move(a, -1));
    else if (k === 'Home' && !searchable) setActive(move(-1, 1));
    else if (k === 'End' && !searchable) setActive(move(shown.length, -1));
    else if (k === 'PageDown') setActive((a) => move(Math.min(shown.length - 2, a + 7), 1));
    else if (k === 'PageUp') setActive((a) => move(Math.max(1, a - 7), -1));
    else if (k === 'Enter' || (k === ' ' && !searchable)) choose(shown[active]);
    else if (k === 'Escape') close();
    else if (k === 'Tab') {
      close(false);
      return;
    } else if (!searchable && k.length === 1 && !e.ctrlKey && !e.metaKey) {
      const i = jump(k);
      if (i >= 0) setActive(i);
    } else return;
    e.preventDefault();
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (open) {
      onListKey(e);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openList(e.key === 'ArrowUp' ? 'last' : undefined);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Closed type-to-pick, like a native select.
      const i = jump(e.key);
      if (i >= 0) choose(shown[i]);
    }
  };
  const optId = (i: number) => `${id}-o${i}`;
  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        className={pickClass(className)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-invalid={props['aria-invalid'] || undefined}
        aria-describedby={props['aria-describedby']}
        aria-label={props['aria-label']}
        disabled={disabled}
        data-open={open || undefined}
        data-value={value}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onTriggerKey}
      >
        <span className={current ? 'pick-value' : 'pick-value muted'}>{current ? (props.render ? props.render(current) : current.label) : (placeholder ?? 'Choose')}</span>
        <span className="pick-chev" aria-hidden="true">
          <Icon name="chevronDown" size="sm" />
        </span>
      </button>
      {open ? (
        <Layer place={place} className="pop-select">
          <div ref={layer} className="pop-inner">
            {searchable ? (
              <div className="pop-search">
                <Icon name="search" size="sm" />
                <input
                  ref={search}
                  type="text"
                  value={query}
                  placeholder="Type to filter"
                  aria-label="Filter the list"
                  aria-controls={listId}
                  aria-activedescendant={active >= 0 ? optId(active) : undefined}
                  role="combobox"
                  aria-expanded="true"
                  aria-autocomplete="list"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onListKey}
                />
              </div>
            ) : null}
            <div
              ref={list}
              id={listId}
              role="listbox"
              tabIndex={searchable ? -1 : 0}
              aria-labelledby={id}
              aria-activedescendant={!searchable && active >= 0 ? optId(active) : undefined}
              className="pop-list"
              onKeyDown={searchable ? undefined : onListKey}
            >
              {shown.length === 0 ? <div className="pop-empty t-caption muted">Nothing matches “{query}”.</div> : null}
              {shown.map((o, i) => (
                <div
                  key={`${o.value}-${i}`}
                  id={optId(i)}
                  data-i={i}
                  role="option"
                  data-value={o.value}
                  aria-selected={o.value === value}
                  aria-disabled={o.disabled || undefined}
                  data-active={i === active || undefined}
                  className="pop-opt"
                  onPointerMove={() => !o.disabled && i !== active && setActive(i)}
                  onClick={() => choose(o)}
                >
                  <span className="grow">{props.render ? props.render(o) : o.label}</span>
                  {o.value === value ? <Icon name="check" size="sm" /> : null}
                </div>
              ))}
            </div>
          </div>
        </Layer>
      ) : null}
    </>
  );
}

// ── Dates: plain YYYY-MM-DD strings, calendar maths in UTC so no time zone can shift a day ─────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n: number) => String(n).padStart(2, '0');
export const isoDay = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const parseDay = (s: string | undefined | null) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00Z`) : null);
const addDays = (s: string, n: number) => isoDay(new Date(Date.parse(`${s}T00:00:00Z`) + n * 86400000));
const addMonths = (s: string, n: number) => {
  const d = parseDay(s)!;
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + n;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return isoDay(new Date(Date.UTC(y, m, Math.min(d.getUTCDate(), last))));
};
/** Today in Manila (the business day), as YYYY-MM-DD. */
export function manilaToday(now = Date.now()) {
  return isoDay(new Date(now + 8 * 3600000));
}
export function formatDay(s: string) {
  const d = parseDay(s);
  if (!d) return '';
  return `${WEEKDAYS[d.getUTCDay()]}, ${d.getUTCDate()} ${(MONTHS[d.getUTCMonth()] ?? '').slice(0, 3)} ${d.getUTCFullYear()}`;
}
/** The 6×7 grid of days shown for the month containing `s`, Sunday first. */
export function monthGrid(s: string): string[] {
  const d = parseDay(s)!;
  const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const start = addDays(isoDay(first), -first.getUTCDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function DateField(props: FieldProps & { min?: string; max?: string }) {
  const { id: idProp, value, onChange, className, disabled, min, max, placeholder } = props;
  const auto = useId();
  const id = idProp ?? `date${auto}`;
  const titleId = `${id}-title`;
  const [open, setOpen] = useState(false);
  const today = manilaToday();
  const clamp = (s: string) => (min && s < min ? min : max && s > max ? max : s);
  const [focus, setFocus] = useState(() => clamp(parseDay(value) ? value : today));
  const trigger = useRef<HTMLButtonElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const place = usePlacement(open, trigger, 320);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }, []);
  useOutside(open, [trigger, layer], () => close(false));
  useEffect(() => {
    if (open && place) grid.current?.querySelector<HTMLElement>('[data-focus="true"]')?.focus({ preventScroll: true });
  }, [open, focus, place]);

  const allowed = (s: string) => !(min && s < min) && !(max && s > max);
  const pick = (s: string) => {
    if (!allowed(s)) return;
    if (s !== value) onChange({ target: { value: s } });
    close();
  };
  const openCal = () => {
    if (disabled) return;
    setFocus(clamp(parseDay(value) ? value : today));
    setOpen(true);
  };
  const onGridKey = (e: KeyboardEvent<HTMLElement>) => {
    const d = parseDay(focus)!;
    const k = e.key;
    let next: string | null = null;
    if (k === 'ArrowLeft') next = addDays(focus, -1);
    else if (k === 'ArrowRight') next = addDays(focus, 1);
    else if (k === 'ArrowUp') next = addDays(focus, -7);
    else if (k === 'ArrowDown') next = addDays(focus, 7);
    else if (k === 'Home') next = addDays(focus, -d.getUTCDay());
    else if (k === 'End') next = addDays(focus, 6 - d.getUTCDay());
    else if (k === 'PageUp') next = addMonths(focus, e.shiftKey ? -12 : -1);
    else if (k === 'PageDown') next = addMonths(focus, e.shiftKey ? 12 : 1);
    else if (k === 'Enter' || k === ' ') pick(focus);
    else if (k === 'Escape') close();
    else return;
    e.preventDefault();
    if (next) setFocus(clamp(next));
  };

  const fd = parseDay(focus)!;
  const month = fd.getUTCMonth();
  const days = monthGrid(focus);
  const prevOk = !min || addDays(isoDay(new Date(Date.UTC(fd.getUTCFullYear(), month, 1))), -1) >= min;
  const nextOk = !max || isoDay(new Date(Date.UTC(fd.getUTCFullYear(), month + 1, 1))) <= max;
  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        className={pickClass(className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={props['aria-invalid'] || undefined}
        aria-describedby={props['aria-describedby']}
        aria-label={props['aria-label']}
        disabled={disabled}
        data-open={open || undefined}
        data-value={value}
        onClick={() => (open ? close() : openCal())}
        onKeyDown={(e) => {
          if (open && e.key === 'Escape') {
            e.preventDefault();
            close();
          } else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openCal();
          }
        }}
      >
        <span className={value ? 'pick-value t-mono' : 'pick-value muted'}>{value ? formatDay(value) : (placeholder ?? 'Choose a date')}</span>
        <span className="pick-chev" aria-hidden="true">
          <Icon name="calendar" size="sm" />
        </span>
      </button>
      {open ? (
        <Layer place={place} className="pop-cal" role="dialog" labelledBy={titleId}>
          <div ref={layer} className="pop-inner cal" onKeyDown={(e) => e.key === 'Escape' && (e.preventDefault(), close())}>
            <div className="cal-head">
              <button type="button" className="cal-nav" aria-label="Previous month" disabled={!prevOk} onClick={() => setFocus(clamp(addMonths(focus, -1)))}>
                <Icon name="chevronLeft" size="sm" />
              </button>
              <h2 id={titleId} className="cal-title" aria-live="polite">
                {MONTHS[month]} <span className="t-mono">{fd.getUTCFullYear()}</span>
              </h2>
              <button type="button" className="cal-nav" aria-label="Next month" disabled={!nextOk} onClick={() => setFocus(clamp(addMonths(focus, 1)))}>
                <Icon name="chevronRight" size="sm" />
              </button>
            </div>
            <div ref={grid} role="grid" aria-labelledby={titleId} className="cal-grid" onKeyDown={onGridKey}>
              <div role="row" className="cal-row">
                {WEEKDAYS.map((w) => (
                  <span key={w} role="columnheader" aria-label={w} className="cal-wd">
                    {w.slice(0, 2)}
                  </span>
                ))}
              </div>
              {Array.from({ length: 6 }, (_, r) => (
                <div role="row" className="cal-row" key={r}>
                  {days.slice(r * 7, r * 7 + 7).map((s) => {
                    const d = parseDay(s)!;
                    const out = d.getUTCMonth() !== month;
                    const ok = allowed(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        role="gridcell"
                        data-day={s}
                        className="cal-day"
                        tabIndex={s === focus ? 0 : -1}
                        data-focus={s === focus}
                        data-out={out || undefined}
                        data-today={s === today || undefined}
                        aria-selected={s === value}
                        aria-current={s === today ? 'date' : undefined}
                        aria-label={formatDay(s)}
                        disabled={!ok}
                        onClick={() => pick(s)}
                      >
                        {d.getUTCDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="cal-foot">
              <button type="button" className="btn btn-quiet cal-today" disabled={!allowed(today)} onClick={() => pick(today)}>
                Today
              </button>
              <span className="t-monocap muted">Manila</span>
            </div>
          </div>
        </Layer>
      ) : null}
    </>
  );
}

// ── Time: a branded list in 15-minute steps, shown on a 12-hour clock, stored as HH:MM ─────────
export function formatTime(hhmm: string) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}
export function timeOptions(step = 15, keep?: string): SelectOption[] {
  const out: SelectOption[] = [];
  for (let t = 0; t < 24 * 60; t += step) {
    const v = `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
    out.push({ value: v, label: formatTime(v) });
  }
  if (keep && /^\d{2}:\d{2}$/.test(keep) && !out.some((o) => o.value === keep)) {
    out.push({ value: keep, label: formatTime(keep) });
    out.sort((a, b) => a.value.localeCompare(b.value));
  }
  return out;
}

export function TimeField(props: FieldProps & { step?: number }) {
  const options = useMemo(() => timeOptions(props.step ?? 15, props.value), [props.step, props.value]);
  return <Select {...props} options={options} searchable={false} placeholder={props.placeholder ?? 'Choose a time'} render={(o) => <span className="t-mono">{o.label}</span>} />;
}
